// react
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

// components
import { Box, Popover as MUIPopover, Portal } from '@mui/material';

// styles
import { StyledBackdrop, TriggerContainer } from './Popover.styles';

// types
import type { PopoverProps } from './Popover.types';

// utils
import { rightClickThroughBackdrops } from './Popover.utils';

// Returns a stable function reference that always calls the latest version of `fn`.
// Prevents infinite loops when callbacks from props are used as effect dependencies.
function useStableCallback<T extends (...args: any[]) => any>(
  fn: T | undefined
) {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: Parameters<T>) => ref.current?.(...args), []);
}

const Popover = ({
  anchorOrigin,
  anchorPosition,
  children,
  childRef,
  clickEvent = 'onClick',
  component,
  id,
  open = false,
  useForcedState = false,
  forceOpen = false,
  forceComponentWidth = false,
  showBackdrop = true,
  useTransition = true,
  onClose,
  onExited,
  onMount,
  onOpen
}: PopoverProps) => {
  'use memo';

  const [isPopoverOpen, setIsPopoverOpen] = useState(open);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

  const isOpen = isPopoverOpen || forceOpen;

  const stableOnOpen = useStableCallback(onOpen);

  const [anchorNode, setAnchorNode] = useState<HTMLElement | null>(null);

  const setAnchorRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) onMount?.(); // commit-time, fine
      setAnchorNode(node); // state update, not a ref write
    },
    [onMount]
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (!useForcedState) {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
    onClose?.();
  };

  const anchorElement = isValidElement(children)
    ? cloneElement(
        children as ReactElement<{ ref?: (node: HTMLElement | null) => void }>,
        { ref: setAnchorRef }
      )
    : children;

  const componentElement = component;

  useLayoutEffect(() => {
    const anchor = childRef?.current ?? anchorNode;
    if (forceComponentWidth && anchor) {
      const { width } = anchor.getBoundingClientRect();
      setAnchorWidth(width);
    }
  }, [forceComponentWidth, isOpen, childRef, anchorNode]);

  useEffect(() => {
    if (!open) setIsPopoverOpen(false);
  }, [open]);

  useEffect(() => {
    if (isOpen) stableOnOpen();
  }, [isOpen, stableOnOpen]);

  return (
    <>
      <TriggerContainer isOpen={isOpen}>
        <MUIPopover
          {...(!useTransition && { transitionDuration: 0 })}
          id={id}
          anchorReference={!!anchorPosition ? 'anchorPosition' : 'anchorEl'}
          anchorPosition={anchorPosition}
          anchorEl={childRef?.current ?? anchorNode}
          anchorOrigin={anchorOrigin}
          closeAfterTransition
          disableRestoreFocus
          onClose={handleClose}
          open={isOpen}
          slotProps={{
            transition: { onExited },
            paper: {
              style: {
                ...(forceComponentWidth && { width: anchorWidth ?? 'auto' }),
                overflow: 'unset',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            },
            backdrop: {
              onContextMenu: (ev) => rightClickThroughBackdrops(ev, handleClose)
            }
          }}
        >
          {componentElement}
        </MUIPopover>

        <Box display="contents" {...{ [clickEvent]: handleClick }}>
          {anchorElement}
        </Box>
      </TriggerContainer>

      {showBackdrop && (
        <Portal>
          <StyledBackdrop open={isOpen} />
        </Portal>
      )}
    </>
  );
};
export default Popover;
